import {
  PRICING,
  TIER_ORDER,
  capitalize,
  getMonthlyPrice,
  getAnnualSavings,
} from '../../config/pricingConfig';
import { SubscriptionStatus } from '../../hooks/useSubscriptionStatus';

type PaidTier = 'starter' | 'pro';

interface PaidTierCardProps {
  tier: PaidTier;
  isAnnual: boolean;
  isLoading: boolean;
  highlight?: boolean;
  subscriptionStatus: SubscriptionStatus | null;
  onSubscribe: (tier: PaidTier) => void;
}

/** Get CTA button label based on current subscription state. */
const getButtonLabel = (
  tier: PaidTier,
  isAnnual: boolean,
  isLoading: boolean,
  subscriptionStatus: SubscriptionStatus | null
): string => {
  const currentTier = subscriptionStatus?.subscriptionTier;
  const currentBilling = subscriptionStatus?.billingPeriod;
  const selectedBilling = isAnnual ? 'annual' : 'monthly';

  // Exact same plan & billing period → "Current Plan"
  if (currentTier === tier && currentBilling === selectedBilling) {
    return 'Current Plan';
  }

  if (isLoading) return 'Loading...';

  // Same tier, different billing → "Switch to Annual"
  if (currentTier === tier) return 'Switch to Annual';

  // Different tier → upgrade / downgrade / new
  const currentLevel = TIER_ORDER[currentTier ?? 'free'];
  const newLevel = TIER_ORDER[tier];
  const tierName = capitalize(tier);

  if (currentLevel > newLevel) return `Downgrade to ${tierName}`;
  if (currentLevel < newLevel && currentLevel > 0)
    return `Upgrade to ${tierName}`;
  return `Get ${tierName}`;
};

export const PaidTierCard = ({
  tier,
  isAnnual,
  isLoading,
  highlight = false,
  subscriptionStatus,
  onSubscribe,
}: PaidTierCardProps) => {
  const config = PRICING[tier];
  const monthlyPrice = getMonthlyPrice(tier, isAnnual ? 'annual' : 'monthly');
  const savings = getAnnualSavings(tier);
  const selectedBilling = isAnnual ? 'annual' : 'monthly';

  const isCurrentPlan =
    subscriptionStatus?.subscriptionTier === tier &&
    subscriptionStatus?.billingPeriod === selectedBilling;

  // Hide button if annual user tries to switch to monthly
  const hideButton =
    subscriptionStatus?.billingPeriod === 'annual' && !isAnnual;

  return (
    <div
      className={`
        flex flex-col rounded-[30px] p-6
        ${
          highlight
            ? 'relative border border-[#ffd803] bg-[rgba(255,236,131,0.5)]'
            : 'bg-[rgba(235,235,235,0.5)]'
        }
      `}
    >
      {highlight && (
        <div
          className={`
            absolute -top-3 left-1/2 -translate-x-1/2 rounded-[4px] bg-[#ffd803]
            px-3 py-1 text-xs text-[#333]
          `}
        >
          Best Value
        </div>
      )}
      <h2 className={`my-3 pl-2 text-base font-light text-[#0d0d0d]`}>
        {config.label}
      </h2>
      <p
        className={`
          my-3 pl-2 text-[32px] leading-tight font-light text-[#0d0d0d]
        `}
      >
        ${monthlyPrice} <span className="text-[18px]">/ month</span>
      </p>
      {isAnnual && (
        <>
          <p className="pl-2 text-sm text-[#333]">
            billed annually at ${config.annual}/year
          </p>
          <p className="pl-2 text-sm font-medium text-green-600">
            Save ${savings}/year
          </p>
        </>
      )}
      <div className="flex-grow">
        <p className={`p-2 text-sm text-[#333]`}>
          - {config.credits} credits refilled monthly
        </p>
        <p className={`p-2 text-sm text-[#333]`}>- Keep unlimited projects</p>
        <p className={`p-2 text-sm text-[#333]`}>
          - Up to 50 systems per PDF or page image
        </p>
        <p className={`p-2 text-sm text-[#333]`}>
          - Up to 300 measures per MusicXML
        </p>
      </div>
      {!hideButton && (
        <button
          onClick={() => onSubscribe(tier)}
          disabled={isLoading || isCurrentPlan}
          className={`
            mt-4 w-full cursor-pointer rounded-full bg-[#333] px-4 py-2.5
            text-[15px] text-white transition-all duration-100
            hover:bg-[#222]
            active:scale-95
            disabled:cursor-not-allowed disabled:opacity-50
          `}
        >
          {getButtonLabel(tier, isAnnual, isLoading, subscriptionStatus)}
        </button>
      )}
    </div>
  );
};
